import { useEffect, useRef, useState } from 'react';
import type { BandwidthTestResult } from '../lib/bandwidth-test';
import { BandwidthTest } from '../lib/bandwidth-test';
import {
  type DiagnosticSuiteResult,
  type TestResult,
  DiagnosticTest,
} from '../lib/diagnostic-test';
import { generateJsonExport } from '../lib/generate-json-export';
import { generateReport, parseBrowserInfo } from '../lib/generate-report';

interface DiagnosticResultsProps {
  result: DiagnosticSuiteResult;
  bandwidthResult?: BandwidthTestResult | null;
  onRunAgain: () => void;
}

export function DiagnosticResults({
  result,
  bandwidthResult,
  onRunAgain,
}: DiagnosticResultsProps) {
  console.log('[DiagnosticResults] Rendering', {
    hasResult: !!result,
    hasBandwidthResult: !!bandwidthResult,
    testA: result?.testA?.goodputOverallMbps,
    testB: result?.testB?.goodputOverallMbps,
    testC: result?.testC?.goodputOverallMbps,
    testD: result?.testD?.goodputOverallMbps,
  });

  const [showAnalysis, setShowAnalysis] = useState(false);
  const [copiedReport, setCopiedReport] = useState(false);

  // Secret key combination: Ctrl+Shift+A to toggle analysis
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        setShowAnalysis((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const copyToClipboard = async () => {
    const report = generateReport(result, bandwidthResult);
    try {
      await navigator.clipboard.writeText(report);
      setCopiedReport(true);
      setTimeout(() => setCopiedReport(false), 2000);
    } catch {
      console.log(report);
    }
  };

  const exportJson = () => {
    const jsonData = generateJsonExport(result, bandwidthResult);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagnostic-results-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full max-w-[720px] mx-auto px-4 sm:px-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-white">
          Test Results
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copyToClipboard}
            className="p-2 text-gray-400 hover:text-white hover:bg-ds-dark-lighter rounded-lg transition-colors"
            title="Copy Report"
          >
            {copiedReport ? (
              <CheckIcon className="w-5 h-5 text-green-400" />
            ) : (
              <ClipboardIcon className="w-5 h-5" />
            )}
          </button>
          <button
            type="button"
            onClick={exportJson}
            className="p-2 text-gray-400 hover:text-white hover:bg-ds-dark-lighter rounded-lg transition-colors"
            title="Export JSON"
          >
            <DownloadIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onRunAgain}
            className="px-4 py-2 bg-ds-primary hover:bg-ds-primary-dark active:scale-95 rounded-lg font-medium text-sm transition-all"
          >
            Run Test
          </button>
        </div>
      </div>

      {/* Analysis - Secret feature, toggle with Ctrl+Shift+A */}
      {showAnalysis && (
        <div className="mb-6">
          <AnalysisCard analysis={result.analysis} />
        </div>
      )}

      {/* Network Environment Warning */}
      {result.networkEnvironment.publicNetworkLikely && (
        <NetworkWarning environment={result.networkEnvironment} />
      )}

      {/* Speed Test Result - Featured at top */}
      {bandwidthResult && <SpeedResultCard result={bandwidthResult} />}

      {/* Test Results Grid - Row 1: Test A & B */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <TestCard result={result.testA} />
        <TestCard result={result.testB} />
      </div>

      {/* Test Results Grid - Row 2: Test C & D */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <TestCard result={result.testC} />
        <TestCard result={result.testD} />
      </div>

      {/* Comparison Table */}
      <ComparisonTable result={result} />

      {/* Device Info - Now at bottom */}
      <DeviceInfoCard
        info={result.deviceInfo}
        environment={result.networkEnvironment}
      />
    </div>
  );
}

// Icons
function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

interface SpeedResultCardProps {
  result: BandwidthTestResult;
}

function SpeedResultCard({ result }: SpeedResultCardProps) {
  console.log('[SpeedResultCard] Rendering', {
    speedMbps: result?.speedMbps,
    latency: result?.latency,
  });
  const displaySpeed = Math.round(result.speedMbps);

  const getBufferbloatRating = () => {
    const ratio = result.bufferbloatRatio;
    if (ratio < 2) return { label: 'Excellent', color: 'text-green-400' };
    if (ratio < 4) return { label: 'Good', color: 'text-emerald-400' };
    if (ratio < 10) return { label: 'Fair', color: 'text-yellow-400' };
    return { label: 'Poor', color: 'text-red-400' };
  };

  const bufferbloatRating = getBufferbloatRating();

  return (
    <div className="bg-ds-primary/10 rounded-xl p-4 sm:p-6 mb-6 border border-ds-primary/30">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Main speed display */}
        <div className="text-center sm:text-left">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Download Speed
          </div>
          <div className="text-5xl sm:text-6xl font-bold text-ds-accent">
            {displaySpeed}
            <span className="text-xl sm:text-2xl text-gray-400 ml-2">Mbps</span>
          </div>
        </div>

        {/* Latency metrics */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
          <div className="bg-ds-dark-lighter/50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500">Latency</div>
            <div className="text-lg sm:text-xl font-semibold text-white">
              {result.latency.unloaded}ms
            </div>
          </div>
          <div className="bg-ds-dark-lighter/50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500">Under Load</div>
            <div className="text-lg sm:text-xl font-semibold text-white">
              {result.latency.loaded}ms
            </div>
          </div>
          <div className="bg-ds-dark-lighter/50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500">Jitter</div>
            <div className="text-lg sm:text-xl font-semibold text-white">
              ±{result.latency.jitter}ms
            </div>
          </div>
        </div>
      </div>

      {/* Bufferbloat indicator */}
      <div className="mt-4 pt-4 border-t border-ds-dark-border/30">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">
            Bufferbloat ({result.bufferbloatRatio}x latency increase)
          </span>
          <span className={`font-medium ${bufferbloatRating.color}`}>
            {bufferbloatRating.label}
          </span>
        </div>
        <div className="mt-2 h-1.5 bg-ds-dark rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              result.bufferbloatRatio < 2
                ? 'bg-ds-accent'
                : result.bufferbloatRatio < 4
                  ? 'bg-emerald-500'
                  : result.bufferbloatRatio < 10
                    ? 'bg-ds-secondary'
                    : 'bg-red-500'
            }`}
            style={{
              width: `${Math.min(100, (result.bufferbloatRatio / 15) * 100)}%`,
            }}
          />
        </div>
      </div>

      {/* Additional stats */}
      <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-gray-400">
        <span>
          Peak:{' '}
          <span className="text-white">
            {Math.round(result.peakSpeedMbps)} Mbps
          </span>
        </span>
        <span>
          Downloaded:{' '}
          <span className="text-white">
            {BandwidthTest.formatBytes(result.downloadedBytes)}
          </span>
        </span>
        <span>
          Duration:{' '}
          <span className="text-white">
            {(result.testDurationMs / 1000).toFixed(1)}s
          </span>
        </span>
      </div>
    </div>
  );
}

interface NetworkWarningProps {
  environment: DiagnosticSuiteResult['networkEnvironment'];
}

function NetworkWarning({ environment }: NetworkWarningProps) {
  return (
    <div className="bg-orange-900/30 border border-orange-700/50 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">⚠️</span>
        <span className="font-semibold text-orange-400">
          {environment.captivePortalDetected
            ? 'Captive Portal Detected'
            : 'Public Network Likely'}
        </span>
      </div>
      <p className="text-sm text-gray-300 mb-2">
        {environment.captivePortalDetected
          ? 'Your network appears to require login. Results may not be accurate until you complete the login process.'
          : 'Your connection shows characteristics of a public or shared network. Results may vary.'}
      </p>
      {environment.indicators.length > 0 && (
        <ul className="text-xs text-gray-400 list-disc list-inside">
          {environment.indicators.map((indicator, idx) => (
            <li key={idx}>{indicator}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface DeviceInfoCardProps {
  info: DiagnosticSuiteResult['deviceInfo'];
  environment: DiagnosticSuiteResult['networkEnvironment'];
}

function DeviceInfoCard({ info, environment }: DeviceInfoCardProps) {
  const browserInfo = parseBrowserInfo(info.userAgent);

  return (
    <div className="bg-ds-dark-lighter/50 rounded-xl p-4 mb-6 border border-ds-dark-border/30">
      <h3 className="text-sm font-medium text-gray-400 mb-3">
        Device & Network Info
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 text-sm">
        <div>
          <div className="text-gray-500 text-xs">Browser</div>
          <div className="text-white">{browserInfo}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">Platform</div>
          <div className="text-white">{info.platform}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">Screen</div>
          <div className="text-white">
            {info.screenWidth}x{info.screenHeight} @{info.devicePixelRatio}x
          </div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">Timezone</div>
          <div className="text-white">{info.timezone}</div>
        </div>
        {info.hardwareConcurrency && (
          <div>
            <div className="text-gray-500 text-xs">CPU Cores</div>
            <div className="text-white">{info.hardwareConcurrency}</div>
          </div>
        )}
        {info.deviceMemory && (
          <div>
            <div className="text-gray-500 text-xs">Memory</div>
            <div className="text-white">{info.deviceMemory} GB</div>
          </div>
        )}
        {info.connection && (
          <>
            <div>
              <div className="text-gray-500 text-xs">Speed Rating</div>
              <div className="text-white uppercase">
                {info.connection.effectiveType}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Est. Bandwidth</div>
              <div className="text-white">{info.connection.downlink} Mbps</div>
            </div>
          </>
        )}
        <div>
          <div className="text-gray-500 text-xs">Network</div>
          <div
            className={
              environment.publicNetworkLikely
                ? 'text-orange-400'
                : 'text-green-400'
            }
          >
            {environment.captivePortalDetected
              ? 'Captive Portal'
              : environment.publicNetworkLikely
                ? 'Public/Shared'
                : 'Private'}
          </div>
        </div>
      </div>
    </div>
  );
}

interface TestCardProps {
  result: TestResult;
}

function TestCard({ result }: TestCardProps) {
  console.log('[TestCard] Rendering', {
    testId: result?.testId,
    goodputOverallMbps: result?.goodputOverallMbps,
  });
  const testLabels: Record<
    string,
    {
      color: string;
      icon: string;
      description: string;
      throughputLabel: string;
    }
  > = {
    A: {
      color: 'text-ds-primary',
      icon: '📊',
      description: 'Baseline',
      throughputLabel: 'Goodput',
    },
    B: {
      color: 'text-ds-accent',
      icon: '⚡',
      description: 'Parallel',
      throughputLabel: 'Goodput',
    },
    // Test C: Throughput is de-emphasized - TTFB is the primary metric
    C: {
      color: 'text-ds-secondary',
      icon: '🔥',
      description: 'Burst',
      throughputLabel: 'Goodput',
    },
    D: {
      color: 'text-ds-pink',
      icon: '📈',
      description: 'Sustained',
      throughputLabel: 'Goodput',
    },
  };

  const label = testLabels[result.testId];
  const isTestC = result.testId === 'C';

  return (
    <div className="bg-ds-dark-lighter/50 rounded-xl p-4 border border-ds-dark-border/30">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{label.icon}</span>
        <span className={`font-semibold ${label.color}`}>
          Test {result.testId}
        </span>
        <span className="text-xs text-gray-500">({label.description})</span>
      </div>
      <div className="text-xs text-gray-400 mb-3">{result.testName}</div>
      {/* Test C: Show TTFB prominently, throughput de-emphasized */}
      {isTestC ? (
        <>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
            {result.aggregated.p50Ttfb}
            <span className="text-base sm:text-lg text-gray-400 ml-1">ms</span>
            <span className="text-xs text-gray-500 ml-2">TTFB p50</span>
          </div>
          <div className="text-sm text-gray-400 mb-1">
            p95: {result.aggregated.p95Ttfb}ms
            <span className="text-xs text-gray-500 ml-2">(tail latency)</span>
          </div>
        </>
      ) : (
        <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
          {Math.round(result.goodputOverallMbps)}
          <span className="text-base sm:text-lg text-gray-400 ml-1">Mbps</span>
        </div>
      )}
      <div className="text-xs text-gray-500">
        {result.fileCount} files •{' '}
        {DiagnosticTest.formatBytes(result.totalBytes)} •{' '}
        {DiagnosticTest.formatDuration(result.totalDurationMs)}
      </div>
    </div>
  );
}

interface ComparisonTableProps {
  result: DiagnosticSuiteResult;
}

/**
 * Format connection timing with observability awareness
 * If <20% observable, show "reused" instead of misleading 0ms
 * If >=20% observable, show median + "(X% observable)"
 */
function formatConnectionTiming(
  medianMs: number,
  observablePercent: number
): { text: string; isReused: boolean } {
  const OBSERVABILITY_THRESHOLD = 20; // 20%

  if (observablePercent < OBSERVABILITY_THRESHOLD) {
    return { text: 'reused', isReused: true };
  }

  return {
    text: `${medianMs}ms (${observablePercent}%)`,
    isReused: false,
  };
}

function ComparisonTable({ result }: ComparisonTableProps) {
  const tests = [result.testA, result.testB, result.testC, result.testD];
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTooltipShow = (
    metricKey: string,
    event: React.MouseEvent | React.FocusEvent
  ) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 4,
      left: Math.max(8, rect.left - 100),
    });
    setShowTooltip(metricKey);
  };

  const handleTooltipHide = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(null);
      setTooltipPosition(null);
    }, 100);
  };

  const cancelTooltipHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Check if all connection timings have low observability (connection reuse)
  const allConnectionsReused = tests.every(
    (testItem) =>
      testItem.aggregated.dnsObservablePercent < 20 &&
      testItem.aggregated.tcpObservablePercent < 20 &&
      testItem.aggregated.tlsObservablePercent < 20
  );

  const TimingLabel = ({
    label,
    metricKey,
    showTooltipIcon,
  }: { label: string; metricKey: string; showTooltipIcon?: boolean }) => {
    return (
      <td className="py-2 pr-4 text-gray-400">
        {label}
        {showTooltipIcon && (
          <button
            type="button"
            className="text-gray-500 hover:text-gray-300 ml-1 align-middle"
            onMouseEnter={(event) => handleTooltipShow(metricKey, event)}
            onMouseLeave={handleTooltipHide}
            onFocus={(event) => handleTooltipShow(metricKey, event)}
            onBlur={handleTooltipHide}
          >
            <InfoIcon className="w-3.5 h-3.5 inline-block" />
          </button>
        )}
      </td>
    );
  };

  return (
    <div className="bg-ds-dark-lighter/50 rounded-xl p-4 mb-6 overflow-x-auto border border-ds-dark-border/30">
      <h3 className="text-sm font-medium text-gray-400 mb-3">
        Detailed Metrics Comparison
      </h3>
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="text-gray-500 border-b border-ds-dark-border">
            <th className="text-center py-2 pr-4">Metric</th>
            <th className="text-right py-2 px-2">Test A</th>
            <th className="text-right py-2 px-2">Test B</th>
            <th className="text-right py-2 px-2">Test C</th>
            <th className="text-right py-2 px-2">Test D</th>
          </tr>
        </thead>
        <tbody className="text-gray-300">
          {/* Goodput (overall) - THE headline metric */}
          <tr className="border-b border-ds-dark-border/50">
            <td className="py-2 pr-4 text-gray-400">Goodput (overall)</td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className={`text-right py-2 px-2 font-medium ${
                  testItem.testId === 'C' ? 'text-amber-500/70' : 'text-white'
                }`}
              >
                {Math.round(testItem.goodputOverallMbps)} Mbps
                {testItem.testId === 'C' && (
                  <span className="text-xs text-amber-600/70 block">n/a*</span>
                )}
              </td>
            ))}
          </tr>

          {/* Per-file throughput (p50) */}
          <tr className="border-b border-ds-dark-border/50">
            <td className="py-2 pr-4 text-gray-400">
              Per-file throughput (p50)
            </td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className={`text-right py-2 px-2 ${
                  testItem.testId === 'C' ? 'text-amber-500/70' : ''
                }`}
              >
                {testItem.aggregated.p50ThroughputMbps} Mbps
              </td>
            ))}
          </tr>

          {/* Per-file throughput (p10) - slow path diagnostic */}
          <tr className="border-b border-ds-dark-border/50">
            <td className="py-2 pr-4 text-gray-400">
              Per-file throughput (p10)
            </td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className={`text-right py-2 px-2 ${
                  testItem.testId === 'C' ? 'text-amber-500/70' : ''
                }`}
              >
                {testItem.aggregated.p10ThroughputMbps} Mbps
              </td>
            ))}
          </tr>

          {/* Connection timing rows - hidden when all reused, shown individually otherwise */}
          {!allConnectionsReused && (
            <>
              <tr className="border-b border-ds-dark-border/50">
                <TimingLabel
                  label="DNS Time"
                  metricKey="dns"
                  showTooltipIcon={true}
                />
                {tests.map((testItem) => {
                  const formatted = formatConnectionTiming(
                    testItem.aggregated.medianDnsTime,
                    testItem.aggregated.dnsObservablePercent
                  );
                  return (
                    <td
                      key={testItem.testId}
                      className={`text-right py-2 px-2 ${
                        formatted.isReused ? 'text-gray-500 italic' : ''
                      }`}
                    >
                      {formatted.text}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-ds-dark-border/50">
                <TimingLabel
                  label="TCP Connect"
                  metricKey="tcp"
                  showTooltipIcon={true}
                />
                {tests.map((testItem) => {
                  const formatted = formatConnectionTiming(
                    testItem.aggregated.medianTcpConnectTime,
                    testItem.aggregated.tcpObservablePercent
                  );
                  return (
                    <td
                      key={testItem.testId}
                      className={`text-right py-2 px-2 ${
                        formatted.isReused ? 'text-gray-500 italic' : ''
                      }`}
                    >
                      {formatted.text}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-ds-dark-border/50">
                <TimingLabel
                  label="TLS Handshake"
                  metricKey="tls"
                  showTooltipIcon={true}
                />
                {tests.map((testItem) => {
                  const formatted = formatConnectionTiming(
                    testItem.aggregated.medianTlsHandshakeTime,
                    testItem.aggregated.tlsObservablePercent
                  );
                  return (
                    <td
                      key={testItem.testId}
                      className={`text-right py-2 px-2 ${
                        formatted.isReused ? 'text-gray-500 italic' : ''
                      }`}
                    >
                      {formatted.text}
                    </td>
                  );
                })}
              </tr>
            </>
          )}

          {/* Connection reuse indicator when all are reused */}
          {allConnectionsReused && (
            <tr className="border-b border-ds-dark-border/50">
              <td className="py-2 pr-4 text-gray-400">
                Connection Setup
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-300 ml-1 align-middle"
                  onMouseEnter={(event) =>
                    handleTooltipShow('connection', event)
                  }
                  onMouseLeave={handleTooltipHide}
                  onFocus={(event) => handleTooltipShow('connection', event)}
                  onBlur={handleTooltipHide}
                >
                  <InfoIcon className="w-3.5 h-3.5 inline-block" />
                </button>
              </td>
              {tests.map((testItem) => (
                <td
                  key={testItem.testId}
                  className="text-right py-2 px-2 text-gray-500 italic"
                >
                  reused
                </td>
              ))}
            </tr>
          )}

          {/* TTFB (p50) - primary latency metric */}
          <tr className="border-b border-ds-dark-border/50">
            <td className="py-2 pr-4 text-gray-400">TTFB (p50)</td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className={`text-right py-2 px-2 ${
                  testItem.testId === 'C' ? 'text-white font-medium' : ''
                }`}
              >
                {testItem.aggregated.p50Ttfb}ms
              </td>
            ))}
          </tr>

          {/* TTFB (p95) - tail latency, especially important for Test C */}
          <tr className="border-b border-ds-dark-border/50">
            <td className="py-2 pr-4 text-gray-400">TTFB (p95)</td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className={`text-right py-2 px-2 ${
                  testItem.testId === 'C' ? 'text-white font-medium' : ''
                }`}
              >
                {testItem.aggregated.p95Ttfb}ms
              </td>
            ))}
          </tr>

          {/* Download Time */}
          <tr>
            <td className="py-2 pr-4 text-gray-400">Download Time (avg)</td>
            {tests.map((testItem) => (
              <td
                key={testItem.testId}
                className="text-right py-2 px-2"
              >
                {testItem.aggregated.avgDownloadDuration.toFixed(0)}ms
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* Test C note */}
      <p className="text-xs text-amber-600/70 mt-3">
        * Test C throughput is not a capacity metric - it measures latency
        overhead for small files.
      </p>

      {/* Tooltip for connection timing explanations */}
      {showTooltip && tooltipPosition && (
        <div
          className="fixed z-50 w-72 p-3 bg-ds-dark border border-ds-dark-border rounded-lg shadow-lg text-xs text-gray-300"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
          onMouseEnter={cancelTooltipHide}
          onMouseLeave={handleTooltipHide}
        >
          <div className="font-medium text-green-400 mb-1">
            Connection Reuse Active
          </div>
          <p>
            "reused" indicates that most requests reused an existing connection,
            so DNS/TCP/TLS overhead was not measurable.
          </p>
          <p className="mt-2">
            When observable, we show the median timing and what percentage of
            requests had measurable values (e.g., "12ms (28%)").
          </p>
          <p className="mt-2 text-gray-400">
            HTTP/2 and HTTP/3 multiplex requests over a single connection,
            making individual connection timings rarely observable.
          </p>
        </div>
      )}
    </div>
  );
}

interface AnalysisCardProps {
  analysis: DiagnosticSuiteResult['analysis'];
}

function AnalysisCard({ analysis }: AnalysisCardProps) {
  const hasIssues = analysis.issues.length > 0;

  return (
    <div
      className={`rounded-xl p-4 ${hasIssues ? 'bg-ds-secondary/20 border border-ds-secondary/50' : 'bg-ds-accent/20 border border-ds-accent/50'}`}
    >
      <h3
        className={`text-sm font-medium mb-3 ${hasIssues ? 'text-ds-secondary' : 'text-ds-accent'}`}
      >
        {hasIssues ? '⚠️ Issues Detected' : '✅ No Issues Detected'}
      </h3>

      {hasIssues ? (
        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-400 mb-2">Problems:</div>
            <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
              {analysis.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-sm text-gray-400 mb-2">Recommendations:</div>
            <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
              {analysis.recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-300">
          Your network connection appears healthy with no significant issues
          detected. All tests performed within expected parameters.
        </p>
      )}

      {/* Detection flags */}
      <div className="flex flex-wrap gap-4 mt-4 text-xs">
        <span
          className={
            analysis.connectionLimitDetected ? 'text-red-400' : 'text-gray-500'
          }
        >
          Connection Limits:{' '}
          {analysis.connectionLimitDetected ? 'Detected' : 'None'}
        </span>
        <span
          className={
            analysis.inspectionOverheadDetected
              ? 'text-red-400'
              : 'text-gray-500'
          }
        >
          Inspection Overhead:{' '}
          {analysis.inspectionOverheadDetected ? 'Detected' : 'None'}
        </span>
        <span
          className={
            analysis.throttlingDetected ? 'text-red-400' : 'text-gray-500'
          }
        >
          Throttling: {analysis.throttlingDetected ? 'Detected' : 'None'}
        </span>
      </div>
    </div>
  );
}
